import { useState, useCallback, useRef, useEffect } from 'react'
import { cn } from '../../lib/utils'
import { Sparkles, Send, Loader2, RotateCcw } from 'lucide-react'
import api from '../../api'
import MarkdownRenderer from '../ui/MarkdownRenderer'

interface AskAnythingProps {
  suggestedQuestions?: string[]
  className?: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

function getToken(): string {
  return localStorage.getItem('agentcfo_access_token') || ''
}

export function AskAnything({ suggestedQuestions = [], className }: AskAnythingProps) {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [streamStatus, setStreamStatus] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [pendingAnswer, setPendingAnswer] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, pendingAnswer])

  const startNewConversation = useCallback(() => {
    setConversationId(null)
    setMessages([])
    setPendingAnswer('')
    setQuery('')
  }, [])

  const handleAsk = useCallback(async (q?: string) => {
    const question = q || query
    if (!question.trim()) return
    setQuery('')
    setLoading(true)
    setPendingAnswer('')
    setStreamStatus('Connecting...')
    setIsStreaming(false)

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: question }])

    // Abort any previous stream
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    let accumulated = ''

    try {
      const response = await fetch('/api/v1/agent-chat/fpa_analyst/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ message: question, conversation_id: conversationId }),
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event = JSON.parse(line.slice(6))

            switch (event.type) {
              case 'status':
                setStreamStatus(event.content)
                break
              case 'tool_call':
                setStreamStatus(`Querying ${event.name.replace(/_/g, ' ')}...`)
                break
              case 'tool_result':
                break
              case 'text_delta':
                if (!isStreaming) setIsStreaming(true)
                setStreamStatus('')
                accumulated += event.content
                setPendingAnswer(accumulated)
                break
              case 'done':
                if (event.conversation_id) {
                  setConversationId(event.conversation_id)
                }
                break
              case 'error':
                accumulated = `Error: ${event.content}`
                setPendingAnswer(accumulated)
                break
            }
          } catch {
            // Partial JSON, skip
          }
        }
      }

      if (!accumulated) {
        accumulated = 'No response received.'
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return
      // Fallback to blocking endpoint
      try {
        const res = await api.post('/agent-chat/fpa_analyst/chat', {
          message: question,
          conversation_id: conversationId,
        })
        accumulated = res.data?.response || res.data?.message || 'No response received.'
        if (res.data?.conversation_id) {
          setConversationId(res.data.conversation_id)
        }
      } catch {
        accumulated = 'Unable to process your question right now. Please try again.'
      }
    } finally {
      // Move pending answer to messages
      if (accumulated) {
        setMessages(prev => [...prev, { role: 'assistant', content: accumulated }])
      }
      setPendingAnswer('')
      setLoading(false)
      setIsStreaming(false)
      setStreamStatus('')
    }
  }, [query, conversationId, isStreaming])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAsk()
    }
  }

  const hasHistory = messages.length > 0

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[var(--accent,var(--accent))]" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Ask Anything
          </span>
          {conversationId && (
            <span className="text-[9px] text-[var(--text-hint,#475569)] ml-2">
              Conversation active
            </span>
          )}
        </div>
        {hasHistory && (
          <button
            onClick={startNewConversation}
            className="flex items-center gap-1 text-[10px] text-[var(--text-hint,#475569)] hover:text-[var(--text-secondary,#94A3B8)] transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            New chat
          </button>
        )}
      </div>

      {/* Message history */}
      {hasHistory && (
        <div
          ref={scrollRef}
          className="space-y-3 max-h-[400px] overflow-y-auto mb-3 pr-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--border-default) transparent' }}
        >
          {messages.map((msg, i) => (
            <div key={i} className={cn(
              'rounded-lg px-4 py-2.5',
              msg.role === 'user'
                ? 'bg-white/[0.03] border border-white/[0.04] ml-8'
                : 'bg-[var(--accent,var(--accent))]/[0.05] border border-[var(--accent,var(--accent))]/10',
            )}>
              {msg.role === 'user' ? (
                <p className="text-[13px] text-[var(--text-primary,#F0F2F5)]">{msg.content}</p>
              ) : (
                <div className="text-[13px] leading-relaxed text-[var(--text-secondary,#94A3B8)]">
                  <MarkdownRenderer content={msg.content} />
                </div>
              )}
            </div>
          ))}

          {/* Streaming answer (not yet committed to messages) */}
          {pendingAnswer && (
            <div className="rounded-lg px-4 py-2.5 bg-[var(--accent,var(--accent))]/[0.05] border border-[var(--accent,var(--accent))]/10">
              <div className="text-[13px] leading-relaxed text-[var(--text-secondary,#94A3B8)]">
                <MarkdownRenderer content={pendingAnswer} />
                {loading && <span className="ml-0.5 animate-pulse text-[var(--accent,var(--accent))]">|</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Streaming status */}
      {streamStatus && (
        <div className="mb-3 flex items-center gap-2 text-[12px] text-[var(--accent,var(--accent))]/70 animate-pulse">
          <Loader2 className="h-3 w-3 animate-spin" />
          {streamStatus}
        </div>
      )}

      {/* Input */}
      <div className={cn(
        'flex items-center gap-3 rounded-lg px-4 py-3',
        'bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/[0.06]',
        'focus-within:border-[var(--accent,var(--accent))]/30 transition-colors',
      )}>
        <Sparkles className="h-4 w-4 text-[var(--accent,var(--accent))] shrink-0 opacity-50" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={hasHistory ? 'Follow up...' : 'Ask anything about your financials...'}
          className={cn(
            'flex-1 bg-transparent border-none outline-none',
            'text-[13px] text-[var(--text-primary,#F0F2F5)]',
            'placeholder:text-[var(--text-hint,#475569)]',
          )}
        />
        <button
          onClick={() => handleAsk()}
          disabled={!query.trim() || loading}
          className={cn(
            'rounded-md p-1.5 transition-colors',
            query.trim()
              ? 'text-[var(--accent,var(--accent))] hover:bg-[var(--accent,var(--accent))]/10'
              : 'text-[var(--text-hint,#475569)]',
            'disabled:opacity-50',
          )}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Suggested questions (only when no history) */}
      {suggestedQuestions.length > 0 && !hasHistory && !loading && (
        <div className="flex flex-wrap gap-2 mt-3">
          {suggestedQuestions.slice(0, 3).map((q, i) => (
            <button
              key={i}
              onClick={() => handleAsk(q)}
              className={cn(
                'text-[11px] px-3 py-1.5 rounded-full border',
                'border-white/[0.06] text-[var(--text-muted,#64748B)]',
                'hover:border-[var(--accent,var(--accent))]/30 hover:text-[var(--text-secondary,#94A3B8)]',
                'transition-colors',
              )}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
